export const DEFAULT_LOCALE = 'en-AU' as const
export const GERMAN_LOCALE = 'de-DE' as const

export interface VisibleLanguageOption {
  locale: string
  nativeName: string
  slug?: string
  canonical?: boolean
}

function getMatchingLanguageOption(
  locale: string,
  languageOptions: Array<VisibleLanguageOption>,
): VisibleLanguageOption | undefined {
  const normalisedLocale = locale.replace(/_/g, '-')
  const exactMatch = languageOptions.find((option) => {
    return normalisedLocale === option.slug || normalisedLocale === option.locale
  })
  if (exactMatch) return exactMatch

  // where locale is unsupported outside the main version
  // it should redirect to base locale prefix like fr-CA to fr
  const language = normalisedLocale.split('-')[0]
  return languageOptions.find((option) => {
    return option.slug === language || option.locale.split('-')[0] === language
  })
}

export function getFullLocale(locale: string, languageOptions: Array<VisibleLanguageOption>): string | undefined {
  const fullLocale = getMatchingLanguageOption(locale, languageOptions)

  return fullLocale?.locale
}

export function getNormalisedLocale(
  unnormalisedLocale: string,
  languageOptions?: Array<VisibleLanguageOption>,
): string | null {
  if (!languageOptions?.length) return null

  const matchingLang = getMatchingLanguageOption(unnormalisedLocale, languageOptions)
  if (matchingLang) return matchingLang.slug ?? matchingLang.locale

  return null
}

export function resolveLocale(
  locale: string | null | undefined,
  languageOptions: Array<VisibleLanguageOption>,
): {
  full: string | null
  normalised: string | null
} {
  if (!locale) return { full: null, normalised: null }

  return {
    full: getFullLocale(locale, languageOptions) ?? null,
    normalised: getNormalisedLocale(locale, languageOptions),
  }
}

export function getVisibleLanguageOptions(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): Array<VisibleLanguageOption> {
  if (remoteConfig?.error) return []
  const value = remoteConfig?.value
  if (!value || typeof value !== 'object' || Array.isArray(value)) return []

  return (value as { visibleLanguages?: Array<VisibleLanguageOption> }).visibleLanguages ?? []
}

export function isEnabledLocale(
  locale: string,
  enabledLocales?: Array<string>,
): boolean {
  return enabledLocales ? enabledLocales.includes(locale) : true
}
